import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState, useEffect } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { Search, X, Film, Plus, Check, Globe, Download, Loader2, Lightbulb } from 'lucide-react';
import { api } from '@watchparty/shared';
import { tmdbImage, workTypeLabel } from '@/lib/utils';
const IMPORT_MESSAGES = [
    'Searching databases...',
    'Fetching details from TMDB...',
    'Looking up Wikidata...',
    'Importing work data...',
    'Almost there...',
];
const FILTER_OPTIONS = [
    { id: 'all', label: 'All', workType: null },
    { id: 'movie', label: 'Movies', workType: 1 },
    { id: 'tv', label: 'Television', workType: 2 },
];
export function DiscoverTab({ partyId, canAddContent, partyWorkIds }) {
    const navigate = useNavigate();
    const [query, setQuery] = useState('');
    const [filter, setFilter] = useState('all');
    const [searchResults, setSearchResults] = useState([]);
    const [isSearching, setIsSearching] = useState(false);
    const [popularWorks, setPopularWorks] = useState([]);
    const [isLoadingPopular, setIsLoadingPopular] = useState(true);
    // External search state
    const [showExternalSearch, setShowExternalSearch] = useState(false);
    const [externalResults, setExternalResults] = useState([]);
    const [isExternalSearching, setIsExternalSearching] = useState(false);
    const [importingTmdbId, setImportingTmdbId] = useState(null);
    const [externalError, setExternalError] = useState(null);
    const activeWorkType = FILTER_OPTIONS.find(f => f.id === filter).workType;
    // Fetch popular works when filter changes
    useEffect(() => {
        let cancelled = false;
        setIsLoadingPopular(true);
        async function fetchPopular() {
            try {
                const params = new URLSearchParams({ pageSize: '40' });
                if (activeWorkType != null)
                    params.set('mediaType', activeWorkType === 1 ? 'movie' : 'tv');
                const res = await api.get(`/works/popular?${params}`);
                if (!cancelled)
                    setPopularWorks(res.items);
            }
            catch {
                // fail silently
            }
            finally {
                if (!cancelled)
                    setIsLoadingPopular(false);
            }
        }
        fetchPopular();
        return () => { cancelled = true; };
    }, [activeWorkType]);
    // Debounced search
    useEffect(() => {
        if (!query.trim()) {
            setSearchResults([]);
            return;
        }
        // Reset external state when query changes
        setShowExternalSearch(false);
        setExternalResults([]);
        setExternalError(null);
        const timer = setTimeout(async () => {
            setIsSearching(true);
            try {
                const params = new URLSearchParams({
                    q: query,
                    pageSize: '20',
                });
                if (activeWorkType != null)
                    params.set('workType', String(activeWorkType));
                const res = await api.get(`/works/search?${params}`);
                setSearchResults(res.items);
            }
            catch {
                setSearchResults([]);
            }
            finally {
                setIsSearching(false);
            }
        }, 350);
        return () => clearTimeout(timer);
    }, [query, activeWorkType]);
    async function handleExternalSearch() {
        setShowExternalSearch(true);
        setIsExternalSearching(true);
        setExternalError(null);
        try {
            const res = await api.get(`/works/external-search?q=${encodeURIComponent(query)}`);
            setExternalResults(res);
        }
        catch {
            setExternalError('Failed to search external sources.');
            setExternalResults([]);
        }
        finally {
            setIsExternalSearching(false);
        }
    }
    async function handleImport(result) {
        setImportingTmdbId(result.tmdbId);
        try {
            const res = await api.post('/works/import', {
                tmdbId: result.tmdbId,
                mediaType: result.mediaType,
            });
            navigate({ to: '/works/$workId', params: { workId: res.workId }, search: { partyId } });
        }
        catch {
            setExternalError('Failed to import this work.');
        }
        finally {
            setImportingTmdbId(null);
        }
    }
    const activeWorks = query.trim() ? searchResults : popularWorks;
    const isLoading = query.trim() ? isSearching : isLoadingPopular;
    const showNoResults = query.trim() && !isSearching && searchResults.length === 0;
    return (_jsxs("div", { children: [_jsxs("div", { style: { position: 'relative', marginBottom: '24px' }, children: [_jsx("div", { style: {
                            position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)',
                            color: 'var(--color-text-muted)', pointerEvents: 'none', display: 'flex',
                        }, children: _jsx(Search, { size: 16 }) }), _jsx("input", { value: query, onChange: e => setQuery(e.target.value), placeholder: "Search movies and shows...", style: {
                            width: '100%',
                            padding: '10px 36px 10px 38px',
                            borderRadius: 'var(--radius-md)',
                            border: '1px solid var(--color-border)',
                            background: 'var(--color-surface)',
                            color: 'var(--color-text)',
                            fontFamily: 'var(--font-family-body)',
                            fontSize: '0.875rem',
                            outline: 'none',
                            boxSizing: 'border-box',
                            transition: 'border-color 80ms ease',
                        }, onFocus: e => (e.target.style.borderColor = 'var(--color-border-strong)'), onBlur: e => (e.target.style.borderColor = 'var(--color-border)') }), query && (_jsx("button", { onClick: () => setQuery(''), style: {
                            position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)',
                            background: 'none', border: 'none', cursor: 'pointer',
                            color: 'var(--color-text-muted)', display: 'flex', alignItems: 'center',
                            padding: '4px',
                        }, children: _jsx(X, { size: 14 }) }))] }), _jsx("div", { style: { display: 'flex', gap: '8px', marginBottom: '20px' }, children: FILTER_OPTIONS.map(opt => {
                    const isActive = filter === opt.id;
                    return (_jsx("button", { onClick: () => setFilter(opt.id), style: {
                            padding: '6px 16px',
                            borderRadius: 'var(--radius-full)',
                            border: '1px solid',
                            borderColor: isActive ? 'var(--color-text)' : 'var(--color-border)',
                            background: isActive ? 'var(--color-text)' : 'transparent',
                            color: isActive ? 'var(--color-bg)' : 'var(--color-text-muted)',
                            fontFamily: 'var(--font-family-body)',
                            fontSize: '0.8rem',
                            fontWeight: 600,
                            cursor: 'pointer',
                            transition: 'all 80ms ease',
                        }, children: opt.label }, opt.id));
                }) }), _jsx("p", { style: {
                    fontFamily: 'var(--font-family-body)',
                    fontSize: '0.75rem', fontWeight: 700,
                    letterSpacing: '0.08em', textTransform: 'uppercase',
                    color: 'var(--color-text-muted)',
                    marginBottom: '16px',
                }, children: query.trim() ? 'Results' : 'Popular' }), isLoading ? (_jsx(DiscoverSkeleton, {})) : activeWorks.length > 0 ? (_jsx("div", { className: "watchlist-grid", children: activeWorks.map(work => (_jsx(DiscoverWorkCard, { work: work, partyId: partyId, canAddContent: canAddContent, isInParty: partyWorkIds?.has(work.workId) ?? false }, work.workId))) })) : null, showNoResults && (_jsx("div", { style: { textAlign: 'center', padding: '32px 0 0' }, children: _jsxs("p", { style: { fontFamily: 'var(--font-family-body)', fontSize: '0.875rem', color: 'var(--color-text-faint)' }, children: ["No results for \u201C", query, "\u201D."] }) })), query.trim() && !isSearching && !showExternalSearch && (_jsx("div", { style: { textAlign: 'center', padding: '24px 0' }, children: _jsxs("button", { onClick: handleExternalSearch, style: {
                        display: 'inline-flex', alignItems: 'center', gap: '8px',
                        padding: '10px 20px',
                        borderRadius: 'var(--radius-full)',
                        border: '1px solid var(--color-border)',
                        background: 'var(--color-surface)',
                        color: 'var(--color-text)',
                        fontFamily: 'var(--font-family-body)',
                        fontSize: '0.8rem', fontWeight: 600,
                        cursor: 'pointer',
                        transition: 'all 120ms ease',
                    }, onMouseEnter: e => {
                        e.currentTarget.style.borderColor = 'var(--color-border-strong)';
                        e.currentTarget.style.background = 'var(--color-surface-raised)';
                    }, onMouseLeave: e => {
                        e.currentTarget.style.borderColor = 'var(--color-border)';
                        e.currentTarget.style.background = 'var(--color-surface)';
                    }, children: [_jsx(Globe, { size: 15 }), "Search external sources..."] }) })), showExternalSearch && (_jsxs("div", { children: [_jsx("div", { style: { marginBottom: '20px' }, children: _jsxs("p", { style: {
                                fontFamily: 'var(--font-family-body)',
                                fontSize: '0.75rem', fontWeight: 700,
                                letterSpacing: '0.08em', textTransform: 'uppercase',
                                color: 'var(--color-text-muted)',
                                display: 'flex', alignItems: 'center', gap: '6px',
                            }, children: [_jsx(Globe, { size: 13 }), "External Results"] }) }), isExternalSearching ? (_jsx(DiscoverSkeleton, {})) : externalError ? (_jsx("div", { style: { textAlign: 'center', padding: '32px 0' }, children: _jsx("p", { style: { fontFamily: 'var(--font-family-body)', fontSize: '0.875rem', color: 'var(--color-danger)' }, children: externalError }) })) : externalResults.length > 0 ? (_jsx("div", { className: "watchlist-grid", children: externalResults.map(result => (_jsx(ExternalWorkCard, { result: result, isImporting: importingTmdbId === result.tmdbId, onImport: () => handleImport(result) }, `${result.mediaType}-${result.tmdbId}`))) })) : (_jsx("div", { style: { textAlign: 'center', padding: '32px 0' }, children: _jsx("p", { style: { fontFamily: 'var(--font-family-body)', fontSize: '0.875rem', color: 'var(--color-text-faint)' }, children: "No external results found either." }) }))] }))] }));
}
// ---------------------------------------------------------------------------
// DiscoverWorkCard
// ---------------------------------------------------------------------------
function DiscoverWorkCard({ work, partyId, canAddContent, isInParty }) {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const [overlayVisible, setOverlayVisible] = useState(false);
    const [isAdding, setIsAdding] = useState(false);
    const [added, setAdded] = useState(isInParty);
    const [suggested, setSuggested] = useState(false);
    async function handleAdd(e) {
        e.stopPropagation();
        if (isAdding || added)
            return;
        setIsAdding(true);
        try {
            await api.post(`/parties/${partyId}/works`, { workId: work.workId });
            setAdded(true);
            queryClient.invalidateQueries({ queryKey: ['party', partyId, 'watchlist'] });
            queryClient.invalidateQueries({ queryKey: ['party', partyId, 'work-ids'] });
        }
        catch {
            // fail silently — work may already be on the list
            setAdded(true);
        }
        finally {
            setIsAdding(false);
        }
    }
    async function handleSuggest(e) {
        e.stopPropagation();
        if (isAdding || suggested)
            return;
        setIsAdding(true);
        try {
            await api.post(`/parties/${partyId}/works/suggest`, { workId: work.workId });
            setSuggested(true);
            queryClient.invalidateQueries({ queryKey: ['party', partyId, 'activity'] });
        }
        catch {
            // fail silently
        }
        finally {
            setIsAdding(false);
        }
    }
    function handleNavigate() {
        navigate({ to: '/works/$workId', params: { workId: work.workId }, search: { partyId } });
    }
    return (_jsxs("div", { style: { position: 'relative', cursor: 'pointer' }, onMouseEnter: () => setOverlayVisible(true), onMouseLeave: () => setOverlayVisible(false), onTouchStart: () => setOverlayVisible(v => !v), onClick: handleNavigate, children: [_jsxs("div", { style: {
                    aspectRatio: '2/3',
                    borderRadius: 'var(--radius-md)',
                    overflow: 'hidden',
                    background: 'var(--color-surface-sunken)',
                    position: 'relative',
                }, children: [work.posterUrl ? (_jsx("img", { src: tmdbImage(work.posterUrl, 'w342'), alt: work.title, loading: "lazy", style: { width: '100%', height: '100%', objectFit: 'cover', display: 'block' } })) : (_jsx("div", { style: { width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }, children: _jsx(Film, { size: 28, style: { color: 'var(--color-text-faint)' } }) })), _jsx("div", { style: {
                            position: 'absolute', inset: 0,
                            background: 'rgba(0,0,0,0.72)',
                            display: 'flex', flexDirection: 'column',
                            alignItems: 'center', justifyContent: 'center',
                            gap: '10px', padding: '12px',
                            opacity: overlayVisible ? 1 : 0,
                            pointerEvents: overlayVisible ? 'auto' : 'none',
                            transition: 'opacity 120ms ease',
                        }, children: canAddContent ? (_jsx("button", { onClick: handleAdd, disabled: isAdding, style: {
                                display: 'flex', alignItems: 'center', gap: '6px',
                                padding: '7px 14px',
                                borderRadius: 'var(--radius-full)',
                                border: 'none',
                                background: added ? 'rgba(255,255,255,0.2)' : 'white',
                                color: added ? 'rgba(255,255,255,0.8)' : '#000',
                                fontFamily: 'var(--font-family-body)',
                                fontSize: '0.75rem', fontWeight: 700,
                                cursor: added ? 'default' : 'pointer',
                                transition: 'background 120ms ease',
                                whiteSpace: 'nowrap',
                            }, children: added
                                ? _jsxs(_Fragment, { children: [_jsx(Check, { size: 13 }), " Added"] })
                                : _jsxs(_Fragment, { children: [_jsx(Plus, { size: 13 }), " Add to List"] }) })) : (_jsx("button", { onClick: handleSuggest, disabled: isAdding, style: {
                                display: 'flex', alignItems: 'center', gap: '6px',
                                padding: '7px 14px',
                                borderRadius: 'var(--radius-full)',
                                border: 'none',
                                background: suggested ? 'rgba(255,255,255,0.2)' : 'white',
                                color: suggested ? 'rgba(255,255,255,0.8)' : '#000',
                                fontFamily: 'var(--font-family-body)',
                                fontSize: '0.75rem', fontWeight: 700,
                                cursor: suggested ? 'default' : 'pointer',
                                transition: 'background 120ms ease',
                                whiteSpace: 'nowrap',
                            }, children: suggested
                                ? _jsxs(_Fragment, { children: [_jsx(Check, { size: 13 }), " Suggested"] })
                                : _jsxs(_Fragment, { children: [_jsx(Lightbulb, { size: 13 }), " Suggest"] }) })) })] }), _jsxs("div", { style: { marginTop: '8px' }, children: [_jsx("p", { style: {
                            margin: 0, fontSize: '0.8rem', fontWeight: 700,
                            color: 'var(--color-text)', fontFamily: 'var(--font-family-body)',
                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        }, children: work.title }), _jsxs("p", { style: { margin: 0, fontSize: '0.7rem', color: 'var(--color-text-muted)', fontFamily: 'var(--font-family-body)' }, children: [work.releaseYear ?? '', work.releaseYear ? ' \u00B7 ' : '', workTypeLabel(work.workType)] })] })] }));
}
// ---------------------------------------------------------------------------
// ExternalWorkCard
// ---------------------------------------------------------------------------
function ExternalWorkCard({ result, isImporting, onImport, }) {
    const [overlayVisible, setOverlayVisible] = useState(false);
    const [messageIndex, setMessageIndex] = useState(0);
    // Cycle through import progress messages
    useEffect(() => {
        if (!isImporting) {
            setMessageIndex(0);
            return;
        }
        const timer = setInterval(() => {
            setMessageIndex(i => (i + 1) % IMPORT_MESSAGES.length);
        }, 1500);
        return () => clearInterval(timer);
    }, [isImporting]);
    const typeLabel = result.mediaType === 'movie' ? 'Movie' : 'Series';
    return (_jsxs("div", { style: { position: 'relative', cursor: 'pointer' }, onMouseEnter: () => setOverlayVisible(true), onMouseLeave: () => setOverlayVisible(false), onTouchStart: () => setOverlayVisible(v => !v), children: [_jsxs("div", { style: {
                    aspectRatio: '2/3',
                    borderRadius: 'var(--radius-md)',
                    overflow: 'hidden',
                    background: 'var(--color-surface-sunken)',
                    position: 'relative',
                }, children: [result.posterUrl ? (_jsx("img", { src: tmdbImage(result.posterUrl, 'w342'), alt: result.title, loading: "lazy", style: { width: '100%', height: '100%', objectFit: 'cover', display: 'block' } })) : (_jsx("div", { style: { width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }, children: _jsx(Film, { size: 28, style: { color: 'var(--color-text-faint)' } }) })), _jsx("div", { style: {
                            position: 'absolute', inset: 0,
                            background: 'rgba(0,0,0,0.72)',
                            display: 'flex', flexDirection: 'column',
                            alignItems: 'center', justifyContent: 'center',
                            gap: '10px', padding: '12px',
                            opacity: overlayVisible || isImporting ? 1 : 0,
                            pointerEvents: overlayVisible || isImporting ? 'auto' : 'none',
                            transition: 'opacity 120ms ease',
                        }, children: isImporting ? (_jsxs(_Fragment, { children: [_jsx(Loader2, { size: 22, style: { color: 'white', animation: 'spin 1s linear infinite' } }), _jsx("p", { style: {
                                        fontFamily: 'var(--font-family-body)',
                                        fontSize: '0.7rem', color: 'rgba(255,255,255,0.7)',
                                        textAlign: 'center',
                                    }, children: IMPORT_MESSAGES[messageIndex] })] })) : (_jsxs("button", { onClick: onImport, style: {
                                display: 'flex', alignItems: 'center', gap: '6px',
                                padding: '7px 14px',
                                borderRadius: 'var(--radius-full)',
                                border: 'none',
                                background: 'white',
                                color: '#000',
                                fontFamily: 'var(--font-family-body)',
                                fontSize: '0.75rem', fontWeight: 700,
                                cursor: 'pointer',
                                transition: 'background 120ms ease',
                                whiteSpace: 'nowrap',
                            }, children: [_jsx(Download, { size: 13 }), "Import"] })) }), _jsxs("div", { style: {
                            position: 'absolute', top: '6px', right: '6px',
                            background: 'rgba(0,0,0,0.7)',
                            borderRadius: 'var(--radius-sm)',
                            padding: '2px 6px',
                            display: 'flex', alignItems: 'center', gap: '4px',
                        }, children: [_jsx(Globe, { size: 10, style: { color: 'rgba(255,255,255,0.7)' } }), _jsx("span", { style: {
                                    fontFamily: 'var(--font-family-body)',
                                    fontSize: '0.6rem', fontWeight: 700,
                                    color: 'rgba(255,255,255,0.7)',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.06em',
                                }, children: "TMDB" })] })] }), _jsxs("div", { style: { marginTop: '8px' }, children: [_jsx("p", { style: {
                            margin: 0, fontSize: '0.8rem', fontWeight: 700,
                            color: 'var(--color-text)', fontFamily: 'var(--font-family-body)',
                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        }, children: result.title }), _jsxs("p", { style: { margin: 0, fontSize: '0.7rem', color: 'var(--color-text-muted)', fontFamily: 'var(--font-family-body)' }, children: [result.releaseYear ?? '', result.releaseYear ? ' \u00B7 ' : '', typeLabel] })] })] }));
}
// ---------------------------------------------------------------------------
// DiscoverSkeleton
// ---------------------------------------------------------------------------
function DiscoverSkeleton() {
    return (_jsx("div", { className: "watchlist-grid", children: Array.from({ length: 12 }).map((_, i) => (_jsxs("div", { children: [_jsx("div", { style: {
                        aspectRatio: '2/3',
                        borderRadius: 'var(--radius-md)',
                        background: 'var(--color-surface-sunken)',
                        animation: 'pulse 1.5s ease-in-out infinite',
                    } }), _jsx("div", { style: { marginTop: '8px', height: '12px', borderRadius: '4px', background: 'var(--color-surface-sunken)', width: '80%', animation: 'pulse 1.5s ease-in-out infinite' } }), _jsx("div", { style: { marginTop: '4px', height: '10px', borderRadius: '4px', background: 'var(--color-surface-sunken)', width: '50%', animation: 'pulse 1.5s ease-in-out infinite' } })] }, i))) }));
}
